import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { hubApi } from '../../lib/hubApi';

export type AdviceCardProps = {
  findingId: string;
  title: string;
  context: string;
  ruleAdvice: string;
  exportListKey?: string;
  onRequestBPX: () => void;
};

export function AdviceCard(props: AdviceCardProps) {
  const [aiText, setAiText] = useState<string | null>(null);
  const [aiTokens, setAiTokens] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAi = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await hubApi.post<{ text: string; tokens: number }>(
        `/data-health/findings/${encodeURIComponent(props.findingId)}/explain`,
      );
      setAiText(data.text);
      setAiTokens(data.tokens);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(axiosErr.response?.data?.error ?? axiosErr.message ?? 'unknown error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-4 bg-surface-2/40">
      <div className="text-sm font-medium">{props.title}</div>
      <div className="text-xs opacity-65 mt-0.5">{props.context}</div>

      <div className="mt-3 p-3 rounded bg-surface-0 border border-border">
        <div className="text-[11px] uppercase tracking-wider opacity-55 mb-1">
          Recommended approach
        </div>
        <p className="text-sm leading-relaxed">{props.ruleAdvice}</p>

        {aiText && (
          <div className="mt-3 pt-3 border-t border-dashed border-border">
            <div className="text-[11px] uppercase tracking-wider text-grow mb-1 flex items-center gap-1">
              <Sparkles size={11} /> Explained in context
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{aiText}</p>
            {aiTokens !== null && (
              <div className="text-[11px] opacity-50 mt-1.5">
                Generated against your audit snapshot · {aiTokens} tokens
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="mt-2 text-xs text-sunset">AI advice failed: {error}</div>}

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {!aiText && (
          <button
            type="button"
            onClick={fetchAi}
            disabled={busy}
            className="px-2.5 py-1 rounded border border-grow/50 bg-grow/15 hover:bg-grow/20 disabled:opacity-50 flex items-center gap-1"
          >
            <Sparkles size={11} /> {busy ? 'Generating…' : 'Explain in context (AI)'}
          </button>
        )}
        {aiText && (
          <button
            type="button"
            onClick={fetchAi}
            disabled={busy}
            className="px-2.5 py-1 rounded border border-border hover:bg-surface-3 disabled:opacity-50 opacity-80"
          >
            Regenerate AI advice
          </button>
        )}
        {props.exportListKey && (
          <button
            type="button"
            disabled
            title="CSV export not yet available, coming in a future release"
            className="px-2.5 py-1 rounded border border-border opacity-50 cursor-not-allowed"
          >
            Export list (CSV)
          </button>
        )}
        <button
          type="button"
          onClick={props.onRequestBPX}
          className="px-2.5 py-1 rounded border border-sfblue/50 bg-sfblue/15 hover:bg-sfblue/25 font-medium"
        >
          Request TYRL help
        </button>
      </div>
    </div>
  );
}
